import CustomerJson, { validateBirthdayBookingCustomerJson, validateBirthdayBookingCustomerJsonAddNew } from "./CustomerJson";
import { BirthdayBookingStatus } from "./BirthdayBookingStatus";
import { Validation } from "./Validation";
import SepaMandateJson, { validateSepaMandate, validateSepaMandateAddNew } from './SepaMandateJson';
import InvoiceInfoJson from "./InvoiceInfoJson";


export default interface BirthdayBookingJson {
   ID: number | null;

   /**
    * Datum und Uhrzeit an dem der Geburtstag ausgeführt werden soll
    */
   beginDateTime: string;

   /**
    * Datum und Uhrzeit an dem der Geburtstag endet. Muss am selben Tag sein.
    */
   endDateTime: string;

   /**
    * Freitextfeld für Alternativtermine etc.
    */
   remarks: string;

   /**
    * Kunde für diesen Geburtstag.
    */
   customer: CustomerJson;

   /**
    * Status der Geburtstagsbuchung
    */
   state: BirthdayBookingStatus;

   /**
    * Gibt an ob ein Mitarbeiter benötigt wird
    */
   isStaffRequired: boolean;

   /**
    * Mitarbeiter der dieses Geburtstag betreut (Wenn einer benötigt wird)
    */
   staffId: number | null;

   /**
    * Gibt an, welches Personal automatisch zugewiesen werden kann. Wenn der Wert null ist, kann jeder Mitarbeiter automatisch zugewiesen werden.
    */
   restrictStaffAssignmentIds: number[] | null;

   /**
    * Kategorie des Programmpunkts den der Kunde aus der Liste ausgwählt hat.
    */
   programCategory: string;

   /**
    * Name des Programmpunkts den der Kunde aus der Liste ausgwählt hat.
    */
   programTitle: string;

   /**
    * Bemerkungen gehörend zum Programmpunkt den der Kunde aus der Liste ausgwählt hat.
    */
   programRemarks: string;

   /**
    * Ort an dem der Geburtstag stattfindet. Kann leer sein wenn der Geburtstag noch in Planung ist.
    */
   location: string;

   /**
    * Angabe der Eltern, wie viele Kinder kommen werden.
    */
   estimatedChildrenCount: number;

   /**
    * Preis zu dem der Geburtstag angeboten wird in Eurocent. Kann null sein wenn der Geburtstag noch in Planung ist.
    */
   price: number | null;

   /**
    * Kosten in Eurocent wenn der Geburtstag storniert wird.
    */
   cancellationFee: number;

   /**
    * Stornierungsbedingungen.
    */
   cancellationRemarks: string;

   /**
    * SEPA-Lastschriftmandat für die Zahlung. Kann auch null sein, wenn eine Bar oder Rechnungszahlung gewünscht ist.
    */
   sepaMandate: SepaMandateJson | null;

   /**
     * Rechnungsinformationen wie Rechnungsnummer.
     */
   invoiceInfos: InvoiceInfoJson[] | null;
}

export type BirthdayBookingStaffAssignmentJson = Pick<BirthdayBookingJson, "ID" | "staffId">;
export type BirthdayBookingLocationAssignmentJson = Pick<BirthdayBookingJson, "ID" | "location" | "price">;
export type BirthdayBookingCustomerViewJson = Omit<BirthdayBookingJson, "staffId" | "sepaMandate" | "restrictStaffAssignmentIds" | "invoiceInfos"> & { "staffName": string | null, "canStillBeModified": boolean; };
export type BirthdayBookingProgramChangeJson = Pick<BirthdayBookingJson, "ID" | "programCategory" | "programTitle" | "programRemarks" | "isStaffRequired" | "restrictStaffAssignmentIds" | "beginDateTime" | "endDateTime">;
export type BirthdayBookingWithOverbookingInfoJson = BirthdayBookingJson & { isTimeSlotOverbooked: boolean, maxCountSimultanous: number; };

export function validateBirthdayBookingJsonBookingForm(item: BirthdayBookingJson, birthdaysCanRequestUntilDaysAhead: number) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateFutureDate("beginDateTime", birthdaysCanRequestUntilDaysAhead)
      .validateDateSameDayLaterTime("endDateTime", "beginDateTime")
      .validateString("remarks", 1000)
      .validateUsingFunction("customer", validateBirthdayBookingCustomerJsonAddNew)
      .validateEquals("state", BirthdayBookingStatus.requestedStaffRequired)
      .validateEquals("staffId", null)
      .validateEquals("location", "")
      .validateNonEmptyString("programCategory", 100)
      .validateNonEmptyString("programTitle", 100)
      .validateInteger("estimatedChildrenCount", 1, 50)
      .validateEquals("price", null)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .validateUsingFunction("sepaMandate", validateSepaMandateAddNew, true)
      .finish();
}

export function validateBirthdayBookingJsonManagement(item: BirthdayBookingJson) {
   return new Validation(item)
      .validateInteger("ID", 0)
      .validateDate("beginDateTime")
      .validateDateSameDayLaterTime("endDateTime", "beginDateTime")
      .validateString("remarks", 1000)
      .validateUsingFunction("customer", validateBirthdayBookingCustomerJson, true)
      .validateInteger("estimatedChildrenCount", 1, 50)
      .validateUsingFunction("sepaMandate", validateSepaMandate, true)
      .validateInteger("price", 0, null, true)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .finish();
}

export function validateBirthdayBookingJsonManagementAdd(item: BirthdayBookingJson) {
   return new Validation(item)
      .validateEquals("ID", null)
      .validateEquals("state", BirthdayBookingStatus.requestedStaffRequired)
      .validateDate("beginDateTime")
      .validateDateSameDayLaterTime("endDateTime", "beginDateTime")
      .validateString("remarks", 1000)
      .validateUsingFunction("customer", validateBirthdayBookingCustomerJson, true)
      .validateNonEmptyString("programCategory", 100)
      .validateNonEmptyString("programTitle", 100)
      .validateInteger("estimatedChildrenCount", 1, 50)
      .validateUsingFunction("sepaMandate", validateSepaMandate, true)
      .validateInteger("price", 0, null, true)
      .validateInteger("cancellationFee", 0)
      .validateString("cancellationRemarks", 500)
      .finish();
}

export function validateBirthdayBookingStaffAssignment(item: BirthdayBookingStaffAssignmentJson) {
   return new Validation(item)
      .validateInteger("ID", 0)
      .validateInteger("staffId", 0, null, true)
      .finish();
}

export function validateBirthdayBookingLocationAssignmentJson(item: BirthdayBookingLocationAssignmentJson) {
   return new Validation(item)
      .validateInteger("ID")
      .validateNonEmptyString("location", 100)
      .validateInteger("price", 0)
      .finish();
}

export function validateBirthdayBookingProgramChangeJson(item: BirthdayBookingProgramChangeJson) {
   return new Validation(item)
      .validateInteger("ID")
      .validateDate("beginDateTime")
      .validateDateSameDayLaterTime("endDateTime", "beginDateTime")
      .validateNonEmptyString("programCategory", 100)
      .validateNonEmptyString("programTitle", 100)
      .finish();
}

export function getBirthdayBookingRequestMinDate(birthdaysCanRequestUntilDaysAhead: number): Date {
   var stopDate = new Date();
   stopDate.setDate(stopDate.getDate() + birthdaysCanRequestUntilDaysAhead + 1);
   stopDate.setHours(0, 0, 0);
   return stopDate;
}